<template>
  <div class="auth-page">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">
          {{ isLoginMode ? 'ログイン' : isRegisterMode ? '新規登録' : isResetMode ? 'パスワードリセット' : 'メール認証' }}
        </h1>
      </div>
    </div>

    <section class="section">
      <div class="container">
        <div class="auth-container">
          
          <!-- 登录表单 -->
          <form v-if="isLoginMode" @submit.prevent="handleLogin" class="auth-form">
            <h2 class="form-title">アカウントにログイン</h2>

            <div v-if="authError" class="error-message">
              {{ authError }}
            </div>
            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <div class="form-group">
              <label for="login-email">メールアドレス</label>
              <input
                type="email"
                id="login-email"
                v-model="loginForm.email"
                required
                placeholder="your@email.com"
              >
            </div>

            <div class="form-group password-group">
              <label for="login-password">パスワード</label>
              <input
                :type="passwordFieldType"
                id="login-password"
                v-model="loginForm.password"
                required
                placeholder="••••••••"
              >
              <button type="button" @click="togglePasswordVisibility" class="toggle-password">
                <i :class="['fas', passwordFieldType === 'password' ? 'fa-eye' : 'fa-eye-slash']"></i>
              </button>
            </div>

            <!-- 验证码组件 -->
            <CaptchaInput
              v-model="loginForm.captcha"
              :has-error="captchaError"
              :error-message="captchaErrorMessage"
              @captcha-loaded="onCaptchaLoaded"
              ref="loginCaptcha"
            />

            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              <span v-if="isLoading">処理中...</span>
              <span v-else>ログイン</span>
            </button>

            <div class="auth-links">
              <button
                type="button"
                @click="switchToResetMode"
                class="btn-link forgot-password-link"
              >
                パスワードをお忘れですか？
              </button>
              <button type="button" @click="switchToRegisterMode" class="btn-link switch-mode-link">
                アカウントをお持ちでないですか？ 新規登録
              </button>
            </div>
          </form>

          <!-- 注册表单 -->
          <form v-if="isRegisterMode" @submit.prevent="handleRegister" class="auth-form">
            <h2 class="form-title">新しいアカウントを作成</h2>

            <div v-if="authError" class="error-message">
              {{ authError }}
            </div>

            <div class="form-group">
              <label for="register-email">メールアドレス</label>
              <input
                type="email"
                id="register-email"
                v-model="registerForm.email"
                required
                placeholder="your@email.com"
              >
            </div>

            <div class="form-group password-group">
              <label for="register-password">パスワード</label>
              <input
                :type="passwordFieldType"
                id="register-password"
                v-model="registerForm.password"
                required
                placeholder="••••••••"
              >
              <button type="button" @click="togglePasswordVisibility" class="toggle-password">
                <i :class="['fas', passwordFieldType === 'password' ? 'fa-eye' : 'fa-eye-slash']"></i>
              </button>
            </div>

            <div class="form-group password-group">
              <label for="register-confirm-password">パスワード確認</label>
              <input
                :type="confirmPasswordFieldType"
                id="register-confirm-password"
                v-model="registerForm.confirmPassword"
                required
                placeholder="••••••••"
              >
              <button type="button" @click="toggleConfirmPasswordVisibility" class="toggle-password">
                <i :class="['fas', confirmPasswordFieldType === 'password' ? 'fa-eye' : 'fa-eye-slash']"></i>
              </button>
            </div>

            <!-- 验证码组件 -->
            <CaptchaInput
              v-model="registerForm.captcha"
              :has-error="captchaError"
              :error-message="captchaErrorMessage"
              @captcha-loaded="onCaptchaLoaded"
              ref="registerCaptcha"
            />

            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              <span v-if="isLoading">処理中...</span>
              <span v-else>登録</span>
            </button>

            <div class="auth-links">
              <button type="button" @click="switchToLoginMode" class="btn-link switch-mode-link">
                すでにアカウントをお持ちですか？ ログイン
              </button>
            </div>
          </form>

          <!-- 邮箱验证表单 -->
          <form v-if="isVerificationMode" @submit.prevent="handleEmailVerification" class="auth-form">
            <h2 class="form-title">メール認証</h2>
            <p class="verification-info">
              {{ pendingEmail }} に認証コードを送信しました。<br>
              メールボックスをご確認の上、6桁の認証コードを入力してください。
            </p>

            <div v-if="authError" class="error-message">
              {{ authError }}
            </div>

            <div class="form-group">
              <label for="verification-code">認証コード</label>
              <input
                type="text"
                id="verification-code"
                v-model="verificationCode"
                required
                placeholder="123456"
                maxlength="6"
                class="verification-input"
              >
            </div>

            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              <span v-if="isLoading">認証中...</span>
              <span v-else>認証する</span>
            </button>

            <div class="auth-links">
              <button
                type="button"
                @click="resendVerificationCode"
                class="btn-link"
                :disabled="resendCooldown > 0"
              >
                <span v-if="resendCooldown > 0">
                  認証コードを再送信 ({{ resendCooldown }}秒後)
                </span>
                <span v-else>認証コードを再送信</span>
              </button>
              <button type="button" @click="switchToRegisterMode" class="btn-link">
                登録画面に戻る
              </button>
            </div>
          </form>

          <!-- 密码重置表单 -->
          <form v-if="isResetMode" @submit.prevent="handlePasswordReset" class="auth-form reset-form">
            <h2 class="form-title">パスワードリセット</h2>
            <p class="reset-info">
              登録したメールアドレスを入力してください。<br>
              パスワードリセット用のリンクを送信します。
            </p>

            <div v-if="authError" class="error-message">
              {{ authError }}
            </div>
            <div v-if="successMessage" class="success-message">
              {{ successMessage }}
            </div>

            <div class="form-group">
              <label for="reset-email">メールアドレス</label>
              <input
                type="email"
                id="reset-email"
                v-model="resetEmail"
                required
                placeholder="your@email.com"
              >
            </div>

            <button type="submit" class="btn btn-primary" :disabled="isLoading">
              <span v-if="isLoading">送信中...</span>
              <span v-else>リセットリンクを送信</span>
            </button>

            <div class="auth-links">
              <button type="button" @click="switchToLoginMode" class="btn-link">
                ログイン画面に戻る
              </button>
            </div>
          </form>

        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onUnmounted } from 'vue';
import { useRouter } from 'vue-router';
import { authService } from '@/api';
import CaptchaInput from '@/components/CaptchaInput.vue';

const router = useRouter();
const mode = ref('login'); // 'login', 'register', 'verification', 'reset'

// 表单数据
const loginForm = reactive({
  email: '',
  password: '',
  captcha: ''
});

const registerForm = reactive({
  email: '',
  password: '',
  confirmPassword: '',
  captcha: ''
});

const verificationCode = ref('');
const resetEmail = ref('');
const pendingEmail = ref('');

// 状态管理
const isLoading = ref(false);
const authError = ref('');
const successMessage = ref('');
const captchaError = ref(false);
const captchaErrorMessage = ref('');
const passwordFieldType = ref('password');
const confirmPasswordFieldType = ref('password');

// 验证码相关
const captchaData = ref({});
const resendCooldown = ref(0);
let resendTimer = null;

// 组件引用
const loginCaptcha = ref(null);
const registerCaptcha = ref(null);

// 计算属性
const isLoginMode = computed(() => mode.value === 'login');
const isRegisterMode = computed(() => mode.value === 'register');
const isVerificationMode = computed(() => mode.value === 'verification');
const isResetMode = computed(() => mode.value === 'reset');

// 模式切换
const clearMessages = () => {
  authError.value = '';
  successMessage.value = '';
  captchaError.value = false;
  captchaErrorMessage.value = '';
};

const switchToLoginMode = () => {
  mode.value = 'login';
  clearMessages();
};

const switchToRegisterMode = () => {
  mode.value = 'register';
  clearMessages();
};

const switchToResetMode = () => {
  mode.value = 'reset';
  clearMessages();
};

const switchToVerificationMode = (email) => {
  mode.value = 'verification';
  pendingEmail.value = email;
  clearMessages();
};

// 密码可见性切换
const togglePasswordVisibility = () => {
  passwordFieldType.value = passwordFieldType.value === 'password' ? 'text' : 'password';
};

const toggleConfirmPasswordVisibility = () => {
  confirmPasswordFieldType.value = confirmPasswordFieldType.value === 'password' ? 'text' : 'password';
};

// 验证码相关
const onCaptchaLoaded = (data) => {
  captchaData.value = data;
};

// 登录处理
const handleLogin = async () => {
  clearMessages();
  
  if (!loginForm.email || !loginForm.password) {
    authError.value = 'メールアドレスとパスワードを入力してください。';
    return;
  }
  
  if (!loginForm.captcha) {
    captchaError.value = true;
    captchaErrorMessage.value = '認証コードを入力してください。';
    return;
  }

  isLoading.value = true;
  
  try {
    // 获取验证码数据
    const captchaInfo = loginCaptcha.value?.getCaptchaData();
    
    console.log('Login - Captcha info:', captchaInfo);
    console.log('Login - Form captcha:', loginForm.captcha);
    
    await authService.login(loginForm.email, loginForm.password, {
      captcha_session_id: captchaInfo?.sessionId,
      captcha_code: loginForm.captcha  // 使用用户输入的验证码
    });
    
    successMessage.value = 'ログインに成功しました！';
    setTimeout(() => {
      router.push('/');
    }, 1000);
    
  } catch (error) {
    console.error('Login error:', error);
    
    // 任何登录失败都刷新验证码并清空验证码输入框
    loginCaptcha.value?.refreshCaptcha();
    loginForm.captcha = '';
    
    if (error.message.includes('captcha') || error.message.includes('Invalid captcha')) {
      captchaError.value = true;
      captchaErrorMessage.value = '認証コードが正しくありません。';
    } else if (error.message.includes('Email not verified')) {
      // 邮箱未验证，跳转到验证页面
      switchToVerificationMode(loginForm.email);
      authError.value = 'メールアドレスが認証されていません。認証コードを入力してください。';
    } else {
      authError.value = error.message || 'ログインに失敗しました。';
    }
  } finally {
    isLoading.value = false;
  }
};

// 注册处理
const handleRegister = async () => {
  clearMessages();
  
  if (!registerForm.email || !registerForm.password || !registerForm.confirmPassword) {
    authError.value = 'すべての項目を入力してください。';
    return;
  }
  
  if (registerForm.password !== registerForm.confirmPassword) {
    authError.value = 'パスワードが一致しません。';
    return;
  }
  
  if (registerForm.password.length < 6) {
    authError.value = 'パスワードは6文字以上で入力してください。';
    return;
  }
  
  if (!registerForm.captcha) {
    captchaError.value = true;
    captchaErrorMessage.value = '認証コードを入力してください。';
    return;
  }

  isLoading.value = true;
  
  try {
    // 获取验证码数据
    const captchaInfo = registerCaptcha.value?.getCaptchaData();
    
    console.log('Register - Captcha info:', captchaInfo);
    console.log('Register - Form captcha:', registerForm.captcha);
    
    const response = await authService.register(registerForm.email, registerForm.password, {
      captcha_session_id: captchaInfo?.sessionId,
      captcha_code: registerForm.captcha  // 使用用户输入的验证码
    });
    
    if (response.requires_verification) {
      switchToVerificationMode(registerForm.email);
      
      // 在开发环境中显示验证码
      if (response.verification_code) {
        successMessage.value = `登録が完了しました。メールで認証コードを確認してください。\n開発環境用認証コード: ${response.verification_code}`;
      } else {
        successMessage.value = '登録が完了しました。メールで認証コードを確認してください。';
      }
    } else {
      successMessage.value = '登録が完了しました！';
      setTimeout(() => {
        router.push('/');
      }, 1000);
    }
    
  } catch (error) {
    console.error('Register error:', error);
    
    // 任何注册失败都刷新验证码并清空验证码输入框
    registerCaptcha.value?.refreshCaptcha();
    registerForm.captcha = '';
    
    if (error.message.includes('captcha') || error.message.includes('Invalid captcha')) {
      captchaError.value = true;
      captchaErrorMessage.value = '認証コードが正しくありません。';
    } else {
      authError.value = error.message || '登録に失敗しました。';
    }
  } finally {
    isLoading.value = false;
  }
};

// 邮箱验证处理
const handleEmailVerification = async () => {
  clearMessages();
  
  if (!verificationCode.value || verificationCode.value.length !== 6) {
    authError.value = '6桁の認証コードを入力してください。';
    return;
  }

  isLoading.value = true;
  
  try {
    await authService.verifyEmail(pendingEmail.value, verificationCode.value);
    
    successMessage.value = 'メール認証が完了しました！';
    setTimeout(() => {
      router.push('/');
    }, 1000);
    
  } catch (error) {
    console.error('Email verification error:', error);
    authError.value = error.message || '認証に失敗しました。';
  } finally {
    isLoading.value = false;
  }
};

// 重新发送验证码
const resendVerificationCode = async () => {
  if (resendCooldown.value > 0) return;
  
  try {
    await authService.resendVerification(pendingEmail.value);
    successMessage.value = '認証コードを再送信しました。';
    
    // 开始倒计时
    resendCooldown.value = 60;
    resendTimer = setInterval(() => {
      resendCooldown.value--;
      if (resendCooldown.value <= 0) {
        clearInterval(resendTimer);
      }
    }, 1000);
    
  } catch (error) {
    console.error('Resend verification error:', error);
    authError.value = error.message || '再送信に失敗しました。';
  }
};

// 密码重置处理
const handlePasswordReset = async () => {
  clearMessages();
  
  if (!resetEmail.value) {
    authError.value = 'メールアドレスを入力してください。';
    return;
  }

  isLoading.value = true;
  
  try {
    await authService.forgotPassword(resetEmail.value);
    successMessage.value = 'パスワードリセットのリンクを送信しました。メールをご確認ください。';
    
  } catch (error) {
    console.error('Password reset error:', error);
    authError.value = error.message || 'リセットリンクの送信に失敗しました。';
  } finally {
    isLoading.value = false;
  }
};

// 组件卸载时清理定时器
onUnmounted(() => {
  if (resendTimer) {
    clearInterval(resendTimer);
  }
});
</script>

<style scoped>
.auth-page {
  min-height: calc(100vh - 80px);
}

.page-header {
  background-color: var(--primary-color);
  color: white;
  padding: 50px 0;
  text-align: center;
  margin-bottom: 30px;
}

.page-title {
  font-size: 28px;
  font-weight: bold;
}

.auth-container {
  max-width: 450px;
  margin: 0 auto;
  padding: 0 15px;
}

.auth-form {
  background-color: #fff;
  padding: 30px 35px;
  border-radius: 10px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
}

.form-title {
  text-align: center;
  margin-bottom: 25px;
  color: #333;
  font-size: 20px;
  font-weight: 600;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: #555;
  font-size: 14px;
}

.form-group input {
  width: 100%;
  padding: 12px 15px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 16px;
  box-sizing: border-box;
}

.form-group input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(255, 143, 171, 0.2);
}

.verification-input {
  text-align: center;
  letter-spacing: 3px;
  font-size: 18px;
  font-weight: bold;
}

.error-message {
  background-color: #ffebee;
  color: #c62828;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.success-message {
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 10px 15px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
  text-align: center;
}

.btn {
  display: inline-block;
  padding: 12px 20px;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  text-align: center;
  border: none;
  font-size: 16px;
  width: 100%;
}

.btn-primary {
  background-color: var(--primary-color);
  color: white;
}

.btn-primary:hover {
  background-color: var(--secondary-color);
}

.btn-primary:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #6c757d;
  color: white;
}

.btn-secondary:hover {
  background-color: #5a6268;
}

.password-group {
  position: relative;
}

.toggle-password {
  position: absolute;
  top: 68%;
  right: 12px;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #aaa;
  padding: 5px;
}

.toggle-password:hover {
  color: #555;
}

.toggle-password i {
  font-size: 16px;
}

.auth-links {
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.btn-link {
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  text-decoration: underline;
  font-size: 14px;
  padding: 5px;
}

.btn-link:hover {
  color: var(--secondary-color);
}

.btn-link:disabled {
  color: #ccc;
  cursor: not-allowed;
}

.verification-info,
.reset-info {
  text-align: center;
  margin-bottom: 20px;
  color: #666;
  font-size: 14px;
  line-height: 1.5;
}
</style> 